import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormGroup,
  FormLabel,
  InputGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { useTheme } from 'styled-components';

interface CustomInputLinkProps {
  name: string;
  title: string;
  value?: string;
  label?: string;
  readOnly?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  required?: boolean;
  textAlign?: 'left' | 'center' | 'right';
  placeholder?: string;
  validateUrl?: boolean;
  allowHttp?: boolean;
  showActions?: boolean;
  copyToClipboard?: boolean;
  goToLink?: boolean;
  'data-testid'?: boolean | string;
}

const CustomInputLink: React.FC<CustomInputLinkProps> = ({
  name,
  title,
  value = '',
  label = '',
  readOnly = false,
  onValidationChange,
  onChange,
  required = false,
  textAlign = 'left',
  placeholder = 'https://',
  validateUrl = true,
  allowHttp = true,
  showActions = true,
  copyToClipboard = true,
  goToLink = true,
  'data-testid': dataTestId,
}) => {
  const [internalValue, setInternalValue] = useState(value);
  const [isValid, setIsValid] = useState(true);
  const theme = useTheme();

  const controlId = name;

  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId === true
        ? `input-link-${controlId}`
        : undefined;

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const urlRegex = /^(https|http):\/\/[^ \"\\]+$/;

  const validate = async (url: string) => {
    if (!validateUrl || !url) {
      setIsValid(true);
      return true;
    }
    if (!allowHttp && url.startsWith('http://')) {
      setIsValid(false);
      return false;
    }
    if (!urlRegex.test(url)) {
      setIsValid(false);
      return false;
    }
  };
  useEffect(() => {
    setInternalValue(value);
    validate(value);
  }, [value, validateUrl, allowHttp]);

  const displayValue =
    !readOnly && !label
      ? internalValue
      : label && internalValue
        ? label
        : internalValue;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInternalValue(newValue);
  };

  const handleBlur = async () => {
    await validate(internalValue);
    if (onChange) {
      onChange({ target: { name, value: internalValue } } as any);
    }
  };

  const handleFocus = () => {
    setIsValid(true);

    if (label && internalValue) {
      setInternalValue(internalValue);
      return;
    }

    if (!internalValue) {
      setInternalValue('https://');
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(internalValue);
  };

  const handleOpen = () => {
    if (isValid && internalValue) {
      window.open(internalValue, '_blank');
    }
  };

  return (
    <FormGroup controlId={name}>
      <FormLabel
        style={{ color: theme.colors.botaoConfirmar }}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {required ? (
          <>
            <span style={{ fontWeight: 'bold' }}>{title}</span>
            <span style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}>
              {' '}
              *
            </span>
          </>
        ) : (
          title
        )}
      </FormLabel>
      {showActions ? (
        <InputGroup>
          <FormControl
            type="text"
            name={name}
            value={displayValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            readOnly={readOnly}
            required={required}
            placeholder={placeholder}
            data-testid={testId}
            style={{
              borderColor: isValid
                ? theme.colors.bordaInput
                : theme.colors.vermelho,
              borderStyle: 'solid',
              borderWidth: '1px',
              borderRightWidth: '0',
              textAlign: textAlign,
              backgroundColor: readOnly ? '#e9ecef' : theme.colors.white,
            }}
          />

          <InputGroup.Text
            style={{
              borderColor: isValid
                ? theme.colors.bordaInput
                : theme.colors.vermelho,
              borderStyle: 'solid',
              borderWidth: '1px',
              borderLeftWidth: '0',
              display: 'flex',
              alignItems: 'center',
              paddingTop: '0.25rem',
              paddingBottom: '0',
              backgroundColor: readOnly ? '#ebf0f8' : theme.colors.white,
            }}
          >
            {copyToClipboard && (
              <OverlayTrigger overlay={<Tooltip>Copiar Link</Tooltip>}>
                <Button
                  variant="light"
                  onClick={handleCopy}
                  disabled={!internalValue}
                  style={{ border: 'none', background: 'transparent' }}
                  data-testid={testId ? `${testId}-copy` : undefined}
                >
                  <span
                    className="material-icons"
                    style={{
                      fontSize: '1.2rem',
                      color: !isValid ? theme.colors.botaoConfirmar : 'inherit',
                    }}
                  >
                    content_copy
                  </span>
                </Button>
              </OverlayTrigger>
            )}

            {goToLink && (
              <OverlayTrigger overlay={<Tooltip>Abrir Link</Tooltip>}>
                <Button
                  variant="light"
                  onClick={handleOpen}
                  disabled={!isValid || !internalValue}
                  style={{ border: 'none', background: 'transparent' }}
                  data-testid={testId ? `${testId}-open` : undefined}
                >
                  <span
                    className="material-icons"
                    style={{
                      fontSize: '1.2rem',
                      color: !isValid ? theme.colors.botaoConfirmar : 'inherit',
                    }}
                  >
                    public
                  </span>
                </Button>
              </OverlayTrigger>
            )}
          </InputGroup.Text>
        </InputGroup>
      ) : (
        <FormControl
          type="text"
          name={name}
          value={internalValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          readOnly={readOnly}
          required={required}
          placeholder={placeholder}
          data-testid={testId}
          style={{
            borderColor: isValid
              ? theme.colors.bordaInput
              : theme.colors.vermelho,
            textAlign: textAlign,
            backgroundColor: readOnly ? '#ebf0f8' : theme.colors.white,
          }}
        />
      )}
    </FormGroup>
  );
};

export default CustomInputLink;
